import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { z } from "zod";
import { Button, Input, Link, MessageBox } from "@/components/ui";
import { useAuth } from "@/context/auth/AuthContext";
import { useFormError } from "@/hooks/useFormError";
import { usePortalNavigationAccess } from "@/hooks/usePortalNavigationAccess";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
import { FORGOT_PASSWORD_PAGE, TRIAL_PAGE } from "@/router/paths";
import { consumeAuthFlashMessage } from "@/services/authRedirect";
const loginSchema = z.object({
    email: z.string().min(1, "Email is required").email("Please enter a valid email"),
    password: z.string().min(1, "Password is required")
});
export default function LoginPage() {
    const { register, handleSubmit, formState: { errors, isSubmitting }, setError, clearErrors } = useForm({
        resolver: zodResolver(loginSchema)
    });
    const { login, clearError, isAuthenticated } = useAuth();
    const { isLoading: isNavigationAccessLoading } = usePortalNavigationAccess(isAuthenticated);
    const handleFormError = useFormError();
    const location = useLocation();
    const [authFlashMessage] = useState(()=>consumeAuthFlashMessage());
    // TODO(router-v7): replace this cast with route-level typed location state when we upgrade.
    const locationState = location.state;
    const sessionExpiredMessage = locationState && typeof locationState.message === "string" ? locationState.message : authFlashMessage;
    const isLoggingIn = isSubmitting || isAuthenticated && isNavigationAccessLoading;
    const onSubmit = async (data)=>{
        try {
            clearError();
            clearErrors("root");
            await login(data);
        } catch (error) {
            handleFormError(error, setError);
        }
    };
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Portal",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "mb-8"
                }),
                /*#__PURE__*/ _jsxs("form", {
                    onSubmit: handleSubmit(onSubmit),
                    className: "w-full flex flex-col gap-4",
                    children: [
                        sessionExpiredMessage && /*#__PURE__*/ _jsx(MessageBox, {
                            message: sessionExpiredMessage,
                            type: "warning"
                        }),
                        errors.root && /*#__PURE__*/ _jsx(MessageBox, {
                            message: errors.root.message,
                            type: "error"
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Email",
                            type: "email",
                            placeholder: "Enter your email",
                            ...register("email", {
                                onChange: ()=>{
                                    if (errors.root) {
                                        clearErrors("root");
                                    }
                                    clearError();
                                }
                            }),
                            errors: errors.email
                        }),
                        /*#__PURE__*/ _jsx(Input, {
                            label: "Password",
                            type: "password",
                            placeholder: "Enter your password",
                            ...register("password", {
                                onChange: ()=>{
                                    if (errors.root) {
                                        clearErrors("root");
                                    }
                                    clearError();
                                }
                            }),
                            errors: errors.password
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "-mt-2 text-right",
                            children: /*#__PURE__*/ _jsx(Link, {
                                href: FORGOT_PASSWORD_PAGE,
                                className: "text-sm",
                                children: "Forgot password?"
                            })
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            type: "submit",
                            variant: "simple",
                            color: "navy-90",
                            className: "w-full",
                            disabled: isLoggingIn,
                            children: isLoggingIn ? "Logging in..." : "Log In"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-8 text-center",
                    children: /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm text-gray-600",
                        children: [
                            "Don't have an account?",
                            " ",
                            /*#__PURE__*/ _jsx(Link, {
                                href: TRIAL_PAGE,
                                children: "Create trial account"
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-4 text-center",
                    children: /*#__PURE__*/ _jsxs("span", {
                        className: "text-sm text-gray-600",
                        children: [
                            "Need help?",
                            " ",
                            /*#__PURE__*/ _jsx(Link, {
                                href: "mailto:sales@qrypt.com",
                                external: true,
                                children: "Contact sales"
                            })
                        ]
                    })
                })
            ]
        })
    });
}
